// EditProject.js — same layout as New Project: jurisdiction, inspections, firm and
// engineer(s), then Section 1 of the jurisdiction's form.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import ProjectInfoForm from '../components/ProjectInfoForm';
import { projectDisciplines, disciplinePaths, rosterFromUser } from '../disciplines';
import { EMPTY_PROJECT, projectToForm, formToProject } from '../projectFields';
import Footer from '../components/Footer';
import '../App.css';

const EditProject = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);
  const [form, setForm]       = useState({ ...EMPTY_PROJECT });
  const [roster, setRoster]   = useState({ engineers: [], firms: [] });
  const [signer, setSigner]   = useState({ firmId: '', electrical: '', structural: '' });
  const [savedDisciplines, setSavedDisciplines] = useState(['electrical']);

  useEffect(() => {
    const load = async () => {
      try {
        const [projectDoc, elecDoc, strDoc, userDoc] = await Promise.all([
          getDoc(doc(db, 'projects', projectId)),
          getDoc(doc(db, 'projects', projectId, 'forms', 'electrical')),
          getDoc(doc(db, 'projects', projectId, 'forms', 'structural')),
          getDoc(doc(db, 'users', currentUser.uid)),
        ]);
        if (projectDoc.exists()) {
          const data = projectDoc.data();
          const disciplines = projectDisciplines(data);
          setForm({ ...projectToForm(data), disciplines });
          setSavedDisciplines(disciplines);
        }
        const e = elecDoc.exists() ? elecDoc.data() : {};
        const s = strDoc.exists() ? strDoc.data() : {};
        // Section 1 additional comments used to live on electrical Project Details
        const oldComments = e.sections?.building_desc_comments?.comment;
        if (projectDoc.exists() && projectDoc.data().buildingComments === undefined && oldComments) {
          setForm(f => ({ ...f, buildingComments: oldComments }));
        }
        setSigner({ firmId: e.firmId || s.firmId || '', electrical: e.engineerId || '', structural: s.engineerId || '' });
        setRoster(rosterFromUser(userDoc.exists() ? userDoc.data() : {}));
      } catch (err) {
        console.error('Error loading project:', err);
        setError('Failed to load project data.');
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.county)  { setError('Select the jurisdiction.'); return; }
    if (!form.address) { setError('Enter the street address.'); return; }
    if (!form.disciplines.length) { setError('Select at least one inspection: Electrical or Structural.'); return; }
    const removed = savedDisciplines.filter(k => !form.disciplines.includes(k));
    if (removed.length && !window.confirm(`Remove ${removed.join(' and ')} from this project? Its photos and form entries are kept and come back if you add it again.`)) return;
    setError('');
    setSaving(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        ...formToProject(form),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
      await Promise.all(form.disciplines.map(k =>
        setDoc(doc(db, 'projects', projectId, 'forms', k),
          { engineerId: signer[k] || '', firmId: signer.firmId || '' }, { merge: true })
      ));
      setSuccess(true);
      setTimeout(() => navigate(disciplinePaths(projectId, form.disciplines[0]).details), 1000);
    } catch (err) {
      console.error('Error saving project:', err);
      setError('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.titleRow}>
            <div>
              <h2 style={styles.title}>Edit Project</h2>
              {form.address && <p style={styles.subtitle}>{form.address}{form.city ? `, ${form.city}` : ''}</p>}
            </div>
            <div style={styles.titleButtons}>
              <button type="button" onClick={() => navigate(disciplinePaths(projectId, savedDisciplines[0]).details)} style={styles.cancelButton}>Cancel</button>
              <button type="submit" form="edit-form" style={styles.submitButton} disabled={saving || success}>
                {saving ? 'Saving...' : success ? '✓ Saved' : 'Save Changes'}
              </button>
            </div>
          </div>

          {error   && <div style={styles.errorMessage}>{error}</div>}
          {success && <div style={styles.successMessage}>✓ Project updated</div>}

          <form id="edit-form" onSubmit={handleSubmit}>
            <ProjectInfoForm form={form} setForm={setForm} roster={roster} signer={signer} setSigner={setSigner} />
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container:      { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem', display: 'flex', justifyContent: 'center' },
  card:           { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: '100%', maxWidth: '900px' },
  titleRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title:          { margin: 0, color: '#112b49', fontSize: '1.5rem' },
  subtitle:       { margin: '0.25rem 0 0', color: '#555', fontSize: '0.9rem' },
  titleButtons:   { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  cancelButton:   { padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' },
  submitButton:   { padding: '0.5rem 1.25rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' },
  errorMessage:   { padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '0.875rem', marginBottom: '1rem' },
  successMessage: { padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #a7f3d0', backgroundColor: '#ecfdf5', color: '#065f46', fontSize: '0.875rem', marginBottom: '1rem' },
};

export default EditProject;
